import { useEffect, useMemo, useRef, useState } from 'react';
import SuperAdminIcon from '../layout/SuperAdminIcon';

const INTERNAL_KEYS = new Set(['_id', '__v', 'createdBy', 'updatedBy']);
const SENSITIVE_KEY = /(password|token|secret|credential|private.?key)/i;

export const humanizeKey = (value = '') => String(value)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/^./, (character) => character.toUpperCase());

export const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return String(amount);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(numeric);
};

export const formatAdminValue = (value, key = '') => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (/(At|Date|From|Until|Expires)$/i.test(key)) return formatDateTime(value);
  if (Array.isArray(value)) {
    if (!value.length) return 'None';
    const readable = value.map((item) => item?.displayName || item?.name || item?.label || item?.code || item?.key || item).filter((item) => typeof item !== 'object');
    return readable.length ? readable.join(', ') : `${value.length} configured`;
  }
  if (typeof value === 'object') {
    const label = value.displayName || value.name || value.label || value.code || value.key;
    return label || `${Object.keys(value).length} configured fields`;
  }
  return String(value).replaceAll('_', ' ');
};

const primaryName = (item = {}) => item.displayName || item.name || item.title || item.code || item.key || item.invoiceNumber || item.eventKey || item.snapshotKey || item.organizationKey || 'Configured record';
const itemStatus = (item = {}) => item.status || item.processingStatus || item.moderationStatus || (item.active === true ? 'Active' : item.active === false ? 'Inactive' : 'Current');
const itemType = (item = {}) => item.productType || item.type || item.billingType || item.kind || item.category || '—';
const itemUpdated = (item = {}) => item.updatedAt || item.createdAt || item.effectiveFrom;

const summaryValue = (item = {}) => item.description
  || item.hypothesis
  || item.subtitle
  || item.stageKey
  || item.scopeType
  || item.benefitType
  || item.eventType
  || item.provider
  || 'Configured';

const includedValue = (item = {}) => {
  if (item.entitlementCount !== undefined) return `${item.entitlementCount} feature${item.entitlementCount === 1 ? '' : 's'}`;
  const direct = item.includedValue || item.credits || item.creditAmount || item.quantity || item.limit;
  if (direct !== undefined) return formatAdminValue(direct, 'includedValue');
  const entitlements = item.entitlements || item.includedEntitlements || item.features;
  return entitlements ? formatAdminValue(entitlements, 'entitlements') : summaryValue(item);
};

const pricingValue = (item = {}) => {
  if (item.contactSales || item.billingType === 'contact_sales') return 'Contact sales';
  if (item.amount !== undefined) return formatCurrency(item.amount, item.currency || 'INR');
  if (item.amountMinor !== undefined) return formatCurrency(Number(item.amountMinor) / 100, item.currency || 'INR');
  if (item.price !== undefined && typeof item.price !== 'object') return formatCurrency(item.price, item.currency || 'INR');
  return item.pricingState || item.priceType || '—';
};

export const redactAdminRecord = (value) => {
  if (Array.isArray(value)) return value.map(redactAdminRecord);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, SENSITIVE_KEY.test(key) ? '[redacted]' : redactAdminRecord(child)]));
};

const overviewFields = (item = {}) => Object.entries(item)
  .filter(([key, value]) => !INTERNAL_KEYS.has(key) && !SENSITIVE_KEY.test(key) && value !== null && value !== undefined && typeof value !== 'object')
  .slice(0, 12);

const StatusChip = ({ value }) => {
  const normalized = String(value || 'Current').toLowerCase();
  const tone = /(active|healthy|complete|success|approved|current|enabled|verified)/.test(normalized)
    ? 'success'
    : /(failed|error|rejected|disabled|expired|revoked)/.test(normalized)
      ? 'danger'
      : /(pending|processing|draft|warning)/.test(normalized) ? 'warning' : 'neutral';
  return <span className={`sa-status-chip sa-status-chip--${tone}`}>{humanizeKey(value || 'Current')}</span>;
};

const RecordDrawer = ({ item, onClose }) => {
  const closeRef = useRef(null);
  const drawerRef = useRef(null);
  const previouslyFocused = useRef(document.activeElement);
  useEffect(() => {
    const restoreTarget = previouslyFocused.current;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab') {
        const focusable = drawerRef.current?.querySelectorAll('button:not([disabled]), summary, a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreTarget?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="sa-drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside ref={drawerRef} className="sa-drawer" role="dialog" aria-modal="true" aria-labelledby="sa-record-title">
        <header>
          <div><span>Record details</span><h2 id="sa-record-title">{primaryName(item)}</h2></div>
          <button ref={closeRef} className="sa-icon-button" type="button" onClick={onClose} aria-label="Close details"><SuperAdminIcon name="close" /></button>
        </header>
        <div className="sa-drawer__body">
          <section><h3>Overview</h3><dl className="sa-detail-list"><div><dt>Status</dt><dd><StatusChip value={itemStatus(item)} /></dd></div><div><dt>Type</dt><dd>{humanizeKey(itemType(item))}</dd></div><div><dt>Created</dt><dd>{formatDateTime(item.createdAt)}</dd></div><div><dt>Updated</dt><dd>{formatDateTime(item.updatedAt)}</dd></div></dl></section>
          <section><h3>Configuration</h3><dl className="sa-detail-list">{overviewFields(item).map(([key, value]) => <div key={key}><dt>{humanizeKey(key)}</dt><dd>{formatAdminValue(value, key)}</dd></div>)}</dl></section>
          <details className="sa-developer-details"><summary>Developer details</summary><pre>{JSON.stringify(redactAdminRecord(item), null, 2)}</pre></details>
        </div>
      </aside>
    </div>
  );
};

const standardColumns = [
  { key: 'record', label: 'Record', render: primaryName },
  { key: 'type', label: 'Type', render: (item) => humanizeKey(itemType(item)) },
  { key: 'status', label: 'Status', render: (item) => <StatusChip value={itemStatus(item)} /> },
  { key: 'summary', label: 'Summary', render: (item) => formatAdminValue(summaryValue(item)) },
  { key: 'updated', label: 'Updated', render: (item) => formatDateTime(itemUpdated(item)) },
];

export const commerceColumns = [
  { key: 'product', label: 'Product', render: primaryName },
  { key: 'type', label: 'Type', render: (item) => humanizeKey(itemType(item)) },
  { key: 'status', label: 'Status', render: (item) => <StatusChip value={itemStatus(item)} /> },
  { key: 'billing', label: 'Billing', render: (item) => humanizeKey(item.billingType || item.billingModel || item.purchaseType || '—') },
  { key: 'price', label: 'Price / state', numeric: true, render: pricingValue },
  { key: 'included', label: 'Included value', render: includedValue },
  { key: 'updated', label: 'Updated', render: (item) => formatDateTime(itemUpdated(item)) },
];

const AdminRecordTable = ({ items = [], columns = standardColumns, emptyMessage = 'No configured records in this section.', emptyAction, onEdit, onLifecycle, lifecycleLabel, onDelete, onHistory, onAdjust }) => {
  const [selected, setSelected] = useState(null);
  const safeItems = useMemo(() => Array.isArray(items) ? items : [], [items]);
  if (!safeItems.length) return <div className="sa-empty-state"><strong>Nothing configured yet</strong><p>{emptyMessage}</p>{emptyAction ? <button className="sa-primary-button" type="button" onClick={emptyAction.onClick}>{emptyAction.label}</button> : null}</div>;

  return (
    <>
      <div className="sa-table-wrap">
        <table className="sa-data-table">
          <thead><tr>{columns.map((column) => <th className={column.numeric ? 'is-numeric' : ''} key={column.key} scope="col">{column.label}</th>)}<th className="sa-table-actions" scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
          <tbody>{safeItems.map((item, index) => (
            <tr key={item._id || item.key || item.code || index}>
              {columns.map((column) => <td className={column.numeric ? 'is-numeric' : ''} key={column.key} title={typeof column.render(item) === 'string' ? column.render(item) : undefined}>{column.render(item)}</td>)}
              <td className="sa-table-actions"><button className="sa-icon-button" type="button" aria-label={`View ${primaryName(item)}`} onClick={() => setSelected(item)}><SuperAdminIcon name="more" /></button>{onEdit ? <button className="sa-tertiary-button" type="button" onClick={() => onEdit(item)}>Edit</button> : null}{onAdjust ? <button className="sa-tertiary-button" type="button" onClick={() => onAdjust(item)}>Issue / adjust</button> : null}{onLifecycle && (lifecycleLabel?.(item) ?? (item.status === 'active' || item.enabled === true ? 'Deactivate' : 'Activate')) ? <button className="sa-tertiary-button" type="button" onClick={() => onLifecycle(item)}>{lifecycleLabel?.(item) ?? (item.status === 'active' || item.enabled === true ? 'Deactivate' : 'Activate')}</button> : null}{onHistory ? <button className="sa-tertiary-button" type="button" onClick={() => onHistory(item)}>History</button> : null}{onDelete ? <button className="sa-tertiary-button" type="button" onClick={() => onDelete(item)}>Delete</button> : null}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {selected ? <RecordDrawer item={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
};

export default AdminRecordTable;
